import {
  type PropsWithChildren,
  type ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { logger } from "@hopper-b2b/datadog";
import { B2B_PORTAL_AUTH_REDIRECT_TO } from "@hopper-b2b/types";
import { fetchUserInfo } from "../../../api/session/userInfo";
import { UserContext } from "../../../App";
import { setNubankDatadogUser } from "../../../utils/setNubankDatadogUser";
import { PATH_AUTH_INVALID_SESSION } from "../../../utils/urlPaths";
import { IMPERSONATE_COOKIE_NAME } from "../../AuthModule/constants";

const ProtectedRoute = (props: PropsWithChildren): ReactElement | null => {
  const { children } = props;
  const [authenticated, setAuthenticated] = useState(false);
  const { sessionInfo, updateSessionInfo } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    const userInfoCall = async () => {
      logger()?.info("NU-18 diag: ProtectedRoute validating session", {
        hasSessionInfo: !!sessionInfo,
        hasHopperToken: !!sessionInfo?.hopperSessionToken,
        hasNubankToken: !!sessionInfo?.nubankAccessToken,
        currentPath: window.location.pathname,
        visibilityState: document.visibilityState,
      });
      try {
        const userInfo = await fetchUserInfo({});
        // Check if impersonation cookie is set
        const cookieRegex = `(^|;)\\s*${IMPERSONATE_COOKIE_NAME}\\s*=\\s*([^;]+)`;
        const impersonateCookie = document.cookie.match(cookieRegex)?.pop();

        updateSessionInfo?.({
          ...sessionInfo,
          userInfoResponse: {
            ...(sessionInfo?.userInfoResponse || {}),
            ...userInfo,
          },
          isImpersonation: impersonateCookie === "true",
        });
        logger()?.info("NU-18 diag: ProtectedRoute session VALID");
        setAuthenticated(true);
      } catch (err: any) {
        logger()?.error("NU-18 diag: ProtectedRoute session FAILED", {
          hasSessionInfo: !!sessionInfo,
          hasHopperToken: !!sessionInfo?.hopperSessionToken,
          errorStatus: err?.response?.status,
          currentPath: window.location.pathname,
        });
        const redirectUrl = `${window.location.pathname}${window.location.search}`;
        if (!redirectUrl.includes("/auth/")) {
          sessionStorage.setItem(B2B_PORTAL_AUTH_REDIRECT_TO, redirectUrl);
        }
        // TODO: before deploying to prod, this should go back to nubank app
        navigate(PATH_AUTH_INVALID_SESSION);
      }
    };
    // userInfo validates both Nequi and Hopper sessions, returning the NequiUserDetails if both are valid.
    if (!authenticated) userInfoCall();
  }, [authenticated, navigate, sessionInfo, updateSessionInfo]);

  useEffect(() => {
    if (authenticated && sessionInfo?.hopperUserId) {
      setNubankDatadogUser({
        id: sessionInfo.hopperUserId,
      });
    }
  }, [authenticated, sessionInfo]);

  return authenticated ? <>{children}</> : null;
};

export default ProtectedRoute;
